import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock3,
  Link2,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import PanelToast from '../components/PanelToast';
import {
  buildPublicBookingUrl,
  isBookingSlugValid,
  normalizeBookingSlug,
  resolveBookingOrigin,
} from '../utils/booking';

const DAY_NAMES = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

const SCHEDULE_TEMPLATES = {
  standard: {
    label: 'Klasyczny salon',
    description: 'Poniedziałek-piątek 09:00-17:00. Prosty start bez przerw.',
    days: {
      1: [['09:00', '17:00']],
      2: [['09:00', '17:00']],
      3: [['09:00', '17:00']],
      4: [['09:00', '17:00']],
      5: [['09:00', '17:00']],
      6: [],
      0: [],
    },
  },
  split: {
    label: 'Z przerwą w środku dnia',
    description: 'Poniedziałek-piątek 09:00-13:00 i 14:00-18:00. Dobre pod lunch.',
    days: {
      1: [['09:00', '13:00'], ['14:00', '18:00']],
      2: [['09:00', '13:00'], ['14:00', '18:00']],
      3: [['09:00', '13:00'], ['14:00', '18:00']],
      4: [['09:00', '13:00'], ['14:00', '18:00']],
      5: [['09:00', '13:00'], ['14:00', '18:00']],
      6: [['09:00', '13:00']],
      0: [],
    },
  },
  late: {
    label: 'Po pracy klientów',
    description: 'Wtorek-piątek 12:00-20:00 i sobota 09:00-14:00.',
    days: {
      1: [],
      2: [['12:00', '20:00']],
      3: [['12:00', '20:00']],
      4: [['12:00', '20:00']],
      5: [['12:00', '20:00']],
      6: [['09:00', '14:00']],
      0: [],
    },
  },
};

const SERVICE_PRESETS = [
  {
    name: 'Konsultacja startowa',
    description: 'Pierwsze spotkanie, omówienie potrzeb i plan dalszych wizyt.',
    duration_minutes: 30,
    price: '80',
    promo_price: '',
    promo_label: '',
    addons: [],
  },
  {
    name: 'Manicure hybrydowy',
    description: 'Stylizacja z opracowaniem skórek i wykończeniem pod skórki.',
    duration_minutes: 75,
    price: '150',
    promo_price: '129',
    promo_label: 'Cena na start',
    addons: [
      { name: 'Zdjęcie poprzedniej stylizacji', duration_minutes: 15, price: 20 },
    ],
  },
  {
    name: 'Zabieg premium',
    description: 'Dłuższa usługa z konsultacją i pielęgnacją domową na koniec.',
    duration_minutes: 90,
    price: '240',
    promo_price: '219',
    promo_label: 'Nowość miesiąca',
    addons: [
      { name: 'Maska regenerująca', duration_minutes: 15, price: 35 },
      { name: 'Ampułka wzmacniająca', duration_minutes: 10, price: 25 },
    ],
  },
];

const SERVICE_BUNDLES = [
  {
    id: 'beauty-start',
    label: 'Pakiet start salonu beauty',
    description: 'Konsultacja, usługa podstawowa i wariant premium z pierwszą promocją.',
    services: [
      SERVICE_PRESETS[0],
      SERVICE_PRESETS[1],
      SERVICE_PRESETS[2],
    ],
  },
  {
    id: 'quick-conversion',
    label: 'Pakiet szybka konwersja',
    description: 'Krótka konsultacja i dwie czytelne usługi dla nowych klientek.',
    services: [
      SERVICE_PRESETS[0],
      {
        name: 'Express touch-up',
        description: 'Krótka wizyta kontrolna lub odświeżenie efektu.',
        duration_minutes: 45,
        price: '110',
        promo_price: '95',
        promo_label: 'Pierwsza wizyta',
        addons: [],
      },
      {
        name: 'Pełna wizyta standard',
        description: 'Najczęściej wybierany wariant regularny do rezerwacji online.',
        duration_minutes: 60,
        price: '170',
        promo_price: '',
        promo_label: '',
        addons: [
          { name: 'Dodatkowa pielęgnacja', duration_minutes: 15, price: 30 },
        ],
      },
    ],
  },
];

const EXCEPTION_PRESETS = [
  {
    id: 'weekday-lunch',
    label: 'Lunch pn-pt przez 4 tygodnie',
    description: 'Codzienna przerwa 13:00-14:00 na 4 tygodnie do przodu.',
    reason: 'Lunch',
    weeks: 4,
    weekdays: [1, 2, 3, 4, 5],
    start_time: '13:00',
    end_time: '14:00',
  },
  {
    id: 'saturday-off',
    label: 'Wolne soboty przez 6 tygodni',
    description: 'Całodniowa blokada sobót dla pracy tylko w tygodniu.',
    reason: 'Prywatne',
    weeks: 6,
    weekdays: [6],
    full_day: true,
  },
  {
    id: 'friday-early-close',
    label: 'Krótsze piątki',
    description: 'Blokada piątków 15:00-20:00 na 6 kolejnych tygodni.',
    reason: 'Prywatne',
    weeks: 6,
    weekdays: [5],
    start_time: '15:00',
    end_time: '20:00',
  },
];

const BLOCK_REASON_OPTIONS = ['Przerwa', 'Lunch', 'Prywatne', 'Dojazd', 'Urlop', 'Inne'];

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

function createTimeSlot(start = '09:00', end = '17:00') {
  return { start_time: start, end_time: end };
}

function createScheduleDaysFromTemplate(templateKey = 'standard') {
  const template = SCHEDULE_TEMPLATES[templateKey] || SCHEDULE_TEMPLATES.standard;

  return DAY_ORDER.map((dayOfWeek) => {
    const sourceSlots = template.days[dayOfWeek] || [];
    return {
      day_of_week: dayOfWeek,
      is_active: sourceSlots.length > 0,
      slots: sourceSlots.map(([start, end]) => createTimeSlot(start, end)),
    };
  });
}

function mapScheduleResponseToDays(entries) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return createScheduleDaysFromTemplate();
  }

  return DAY_ORDER.map((dayOfWeek) => {
    const dayEntries = entries
      .filter((entry) => entry.day_of_week === dayOfWeek)
      .sort((left, right) => String(left.start_time).localeCompare(String(right.start_time)));

    return {
      day_of_week: dayOfWeek,
      is_active: dayEntries.length > 0,
      slots: dayEntries.length > 0
        ? dayEntries.map((entry) => createTimeSlot(String(entry.start_time).slice(0, 5), String(entry.end_time).slice(0, 5)))
        : [],
    };
  });
}

function flattenSchedule(days) {
  return days.flatMap((day) => {
    if (!day.is_active) {
      return [];
    }

    return day.slots.map((slot) => ({
      day_of_week: day.day_of_week,
      start_time: slot.start_time,
      end_time: slot.end_time,
      is_active: true,
    }));
  });
}

function createEmptyAddon() {
  return {
    name: '',
    duration_minutes: 15,
    price: '',
  };
}

function createEmptyServiceForm() {
  return {
    name: '',
    description: '',
    duration_minutes: 60,
    price: '',
    promo_price: '',
    promo_label: '',
    addons: [],
  };
}

function createEmptyBreakForm() {
  return {
    reason: 'Przerwa',
    date: getTodayDate(),
    start_time: '13:00',
    end_time: '14:00',
  };
}

function sanitizeAddons(addons) {
  return addons
    .map((addon) => ({
      name: String(addon.name || '').trim(),
      duration_minutes: Number(addon.duration_minutes),
      price: Number(addon.price || 0),
    }))
    .filter((addon) => addon.name && addon.duration_minutes > 0 && addon.price >= 0);
}

function getServicePriceMeta(service) {
  const basePrice = Number(service.price || 0);
  const promoPrice = service.promo_price === '' || service.promo_price == null ? null : Number(service.promo_price);
  const hasPromo = Number.isFinite(promoPrice) && promoPrice > 0 && promoPrice < basePrice;

  return {
    basePrice,
    promoPrice,
    hasPromo,
    effectivePrice: hasPromo ? promoPrice : basePrice,
  };
}

function formatPrice(value) {
  return Number(value || 0).toLocaleString('pl-PL');
}

function sortBlocks(items) {
  return [...items].sort((left, right) => String(left.blocked_from).localeCompare(String(right.blocked_from)));
}

function formatBlockLabel(block) {
  const start = new Date(block.blocked_from);
  const end = new Date(block.blocked_until);
  const sameDay = start.toISOString().slice(0, 10) === end.toISOString().slice(0, 10);

  if (block.block_type === 'vacation' || String(block.reason || '').trim().toLowerCase() === 'urlop') {
    return `${start.toLocaleDateString('pl-PL')} - cały dzień`;
  }

  if (sameDay) {
    return `${start.toLocaleDateString('pl-PL')} · ${String(block.blocked_from).slice(11, 16)}-${String(block.blocked_until).slice(11, 16)}`;
  }

  return `${start.toLocaleDateString('pl-PL')} ${String(block.blocked_from).slice(11, 16)} - ${end.toLocaleDateString('pl-PL')} ${String(block.blocked_until).slice(11, 16)}`;
}

function createExceptionDates({ weeks, weekdays }) {
  const now = new Date();
  const items = [];

  for (let dayOffset = 0; dayOffset < weeks * 7; dayOffset += 1) {
    const nextDate = new Date(now);
    nextDate.setDate(now.getDate() + dayOffset);
    if (weekdays.includes(nextDate.getDay())) {
      items.push(nextDate.toISOString().slice(0, 10));
    }
  }

  return items;
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { professional, updateProfessional } = useAuth();
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [slugDraft, setSlugDraft] = useState(() => professional?.booking_slug || '');
  const [slugError, setSlugError] = useState('');
  const [serviceForm, setServiceForm] = useState(createEmptyServiceForm());
  const [serviceError, setServiceError] = useState('');
  const [scheduleDays, setScheduleDays] = useState(createScheduleDaysFromTemplate());
  const [scheduleError, setScheduleError] = useState('');
  const [breakForm, setBreakForm] = useState(createEmptyBreakForm());
  const [breakError, setBreakError] = useState('');
  const [services, setServices] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        const [servicesResponse, scheduleResponse, blocksResponse] = await Promise.all([
          api.get('/services/mine'),
          api.get('/availability/schedule'),
          api.get('/availability/blocks'),
        ]);

        setServices(Array.isArray(servicesResponse.data) ? servicesResponse.data : []);
        setScheduleDays(mapScheduleResponseToDays(scheduleResponse.data));
        setBlocks(sortBlocks(Array.isArray(blocksResponse.data) ? blocksResponse.data : []));
      } catch {
        setToast({ type: 'error', message: 'Nie udało się pobrać danych startowych.' });
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, []);

  useEffect(() => {
    if (searchParams.get('welcome') === '1') {
      setToast({
        type: 'success',
        message: 'Konto gotowe. Ustaw link, ofertę i grafik, żeby od razu zacząć przyjmować rezerwacje.',
      });
    }
  }, [searchParams]);

  const bookingUrl = useMemo(
    () => buildPublicBookingUrl(professional?.booking_slug || slugDraft),
    [professional?.booking_slug, slugDraft]
  );
  const activeServices = services.filter((service) => service.is_active !== false);
  const activeScheduleDays = scheduleDays.filter((day) => day.is_active && day.slots.length > 0).length;
  const activeScheduleWindows = scheduleDays.reduce((sum, day) => sum + (day.is_active ? day.slots.length : 0), 0);
  const totalAddons = services.reduce((sum, service) => sum + (service.addons?.length || 0), 0);
  const discountedServices = services.filter((service) => getServicePriceMeta(service).hasPromo).length;

  async function handleSaveSlug() {
    const nextSlug = normalizeBookingSlug(slugDraft);
    if (!isBookingSlugValid(nextSlug)) {
      setSlugError('Adres musi mieć 3-50 znaków i składać się z małych liter, cyfr lub myślników.');
      return false;
    }

    setSaving(true);
    setSlugError('');

    try {
      const { data } = await api.patch('/auth/me/booking-profile', { booking_slug: nextSlug });
      updateProfessional(data);
      setSlugDraft(data.booking_slug || nextSlug);
      setToast({ type: 'success', message: 'Adres rezerwacji został zapisany.' });
      return true;
    } catch (err) {
      setSlugError(err.response?.data?.error || 'Nie udało się zapisać adresu rezerwacji.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function createService(serviceInput) {
    const addons = sanitizeAddons(serviceInput.addons);

    return api.post('/services', {
      name: String(serviceInput.name || '').trim(),
      description: String(serviceInput.description || '').trim(),
      duration_minutes: Number(serviceInput.duration_minutes),
      price: Number(serviceInput.price),
      promo_price: serviceInput.promo_price === '' || serviceInput.promo_price == null ? null : Number(serviceInput.promo_price),
      promo_label: String(serviceInput.promo_label || '').trim() || null,
      addons,
    });
  }

  async function handleCreateService() {
    if (!serviceForm.name.trim() || Number(serviceForm.duration_minutes) < 5 || Number(serviceForm.price) <= 0) {
      setServiceError('Uzupełnij nazwę, czas i cenę usługi.');
      return false;
    }

    if (serviceForm.promo_price !== '' && Number(serviceForm.promo_price) >= Number(serviceForm.price || 0)) {
      setServiceError('Cena promocyjna powinna być niższa od regularnej.');
      return false;
    }

    if (serviceForm.addons.some((addon) => addon.name.trim() && (Number(addon.duration_minutes) <= 0 || Number(addon.price) < 0))) {
      setServiceError('Każdy dodatek musi mieć nazwę, czas i nieujemną cenę.');
      return false;
    }

    setSaving(true);
    setServiceError('');

    try {
      const { data } = await createService(serviceForm);
      setServices((prev) => [...prev, data]);
      setServiceForm(createEmptyServiceForm());
      setToast({ type: 'success', message: 'Usługa została dodana do oferty.' });
      return true;
    } catch (err) {
      setServiceError(err.response?.data?.error || 'Nie udało się dodać usługi.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleAddBundle(bundle) {
    setSaving(true);
    setServiceError('');

    try {
      const createdServices = [];

      for (const service of bundle.services) {
        const { data } = await createService(service);
        createdServices.push(data);
      }

      setServices((prev) => [...prev, ...createdServices]);
      setToast({ type: 'success', message: `Dodano ${createdServices.length} usług z pakietu startowego.` });
    } catch (err) {
      setServiceError(err.response?.data?.error || 'Nie udało się dodać pakietu usług.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveSchedule() {
    const normalizedSchedule = flattenSchedule(scheduleDays);

    if (normalizedSchedule.length === 0) {
      setScheduleError('Włącz przynajmniej jeden dzień i jedno okno pracy.');
      return false;
    }

    const hasInvalidWindow = normalizedSchedule.some((slot) => !slot.start_time || !slot.end_time || slot.start_time >= slot.end_time);
    if (hasInvalidWindow) {
      setScheduleError('Każde okno pracy musi mieć poprawny zakres godzin.');
      return false;
    }

    setSaving(true);
    setScheduleError('');

    try {
      await api.put('/availability/schedule', { schedule: normalizedSchedule });
      setToast({ type: 'success', message: 'Grafik startowy został zapisany.' });
      return true;
    } catch (err) {
      setScheduleError(err.response?.data?.error || 'Nie udało się zapisać grafiku.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleAddBlock() {
    if (!breakForm.date || (breakForm.reason !== 'Urlop' && (!breakForm.start_time || !breakForm.end_time))) {
      setBreakError('Uzupełnij datę oraz godziny blokady.');
      return;
    }

    if (breakForm.reason !== 'Urlop' && breakForm.start_time >= breakForm.end_time) {
      setBreakError('Koniec blokady musi być po jej początku.');
      return;
    }

    setSaving(true);
    setBreakError('');

    try {
      const payload = breakForm.reason === 'Urlop'
        ? {
          reason: breakForm.reason,
          block_type: 'vacation',
          blocked_from: `${breakForm.date}T00:00`,
          blocked_until: `${breakForm.date}T23:59`,
        }
        : {
          reason: breakForm.reason,
          blocked_from: `${breakForm.date}T${breakForm.start_time}`,
          blocked_until: `${breakForm.date}T${breakForm.end_time}`,
        };

      const { data } = await api.post('/availability/blocks', payload);
      setBlocks((prev) => sortBlocks([...prev, data]));
      setBreakForm(createEmptyBreakForm());
      setToast({ type: 'success', message: 'Blokada czasu została dodana.' });
    } catch (err) {
      setBreakError(err.response?.data?.error || 'Nie udało się dodać blokady czasu.');
    } finally {
      setSaving(false);
    }
  }

  async function handleApplyExceptionPreset(preset) {
    setSaving(true);
    setBreakError('');

    try {
      const dates = createExceptionDates(preset);
      const createdBlocks = [];
      let skippedCount = 0;

      for (const date of dates) {
        const payload = preset.full_day
          ? {
            reason: preset.reason,
            block_type: 'vacation',
            blocked_from: `${date}T00:00`,
            blocked_until: `${date}T23:59`,
          }
          : {
            reason: preset.reason,
            blocked_from: `${date}T${preset.start_time}`,
            blocked_until: `${date}T${preset.end_time}`,
          };

        try {
          const { data } = await api.post('/availability/blocks', payload);
          createdBlocks.push(data);
        } catch (err) {
          if (err.response?.status === 409) {
            skippedCount += 1;
          } else {
            throw err;
          }
        }
      }

      if (createdBlocks.length > 0) {
        setBlocks((prev) => sortBlocks([...prev, ...createdBlocks]));
      }

      setToast({
        type: 'success',
        message: skippedCount > 0
          ? `Dodano ${createdBlocks.length} blokad, pominięto ${skippedCount} nachodzących wyjątków.`
          : `Dodano ${createdBlocks.length} blokad z szablonu.`,
      });
    } catch (err) {
      setBreakError(err.response?.data?.error || 'Nie udało się zastosować szablonu wyjątków.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteBlock(blockId) {
    setSaving(true);
    try {
      await api.delete(`/availability/blocks/${blockId}`);
      setBlocks((prev) => prev.filter((block) => block.id !== blockId));
      setToast({ type: 'success', message: 'Blokada czasu została usunięta.' });
    } catch (err) {
      setToast({ type: 'error', message: err.response?.data?.error || 'Nie udało się usunąć blokady.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleNext() {
    if (step === 0) {
      const ok = await handleSaveSlug();
      if (ok) {
        setStep(1);
      }
      return;
    }

    if (step === 1) {
      if (activeServices.length > 0) {
        setStep(2);
        return;
      }

      const ok = await handleCreateService();
      if (ok) {
        setStep(2);
      }
      return;
    }

    if (step === 2) {
      const ok = await handleSaveSchedule();
      if (ok) {
        setStep(3);
      }
    }
  }

  function applyServicePreset(preset) {
    setServiceForm({
      name: preset.name,
      description: preset.description,
      duration_minutes: preset.duration_minutes,
      price: preset.price,
      promo_price: preset.promo_price || '',
      promo_label: preset.promo_label || '',
      addons: preset.addons.map((addon) => ({ ...addon })),
    });
    setServiceError('');
  }

  function applyScheduleTemplate(templateKey) {
    setScheduleDays(createScheduleDaysFromTemplate(templateKey));
    setScheduleError('');
  }

  function toggleDay(dayOfWeek) {
    setScheduleDays((prev) => prev.map((day) => {
      if (day.day_of_week !== dayOfWeek) {
        return day;
      }

      if (day.is_active) {
        return { ...day, is_active: false };
      }

      return {
        ...day,
        is_active: true,
        slots: day.slots.length > 0 ? day.slots : [createTimeSlot()],
      };
    }));
  }

  function updateDaySlot(dayOfWeek, slotIndex, field, value) {
    setScheduleDays((prev) => prev.map((day) => (
      day.day_of_week === dayOfWeek
        ? {
          ...day,
          slots: day.slots.map((slot, index) => (
            index === slotIndex ? { ...slot, [field]: value } : slot
          )),
        }
        : day
    )));
    setScheduleError('');
  }

  function addDaySlot(dayOfWeek) {
    setScheduleDays((prev) => prev.map((day) => (
      day.day_of_week === dayOfWeek
        ? { ...day, is_active: true, slots: [...day.slots, createTimeSlot('14:00', '18:00')] }
        : day
    )));
    setScheduleError('');
  }

  function removeDaySlot(dayOfWeek, slotIndex) {
    setScheduleDays((prev) => prev.map((day) => {
      if (day.day_of_week !== dayOfWeek) {
        return day;
      }

      const nextSlots = day.slots.filter((_, index) => index !== slotIndex);
      return {
        ...day,
        is_active: nextSlots.length > 0,
        slots: nextSlots,
      };
    }));
  }

  function addAddonRow() {
    setServiceForm((prev) => ({
      ...prev,
      addons: [...prev.addons, createEmptyAddon()],
    }));
  }

  function updateAddon(index, field, value) {
    setServiceForm((prev) => ({
      ...prev,
      addons: prev.addons.map((addon, addonIndex) => (
        addonIndex === index ? { ...addon, [field]: value } : addon
      )),
    }));
  }

  function removeAddon(index) {
    setServiceForm((prev) => ({
      ...prev,
      addons: prev.addons.filter((_, addonIndex) => addonIndex !== index),
    }));
  }

  if (loading) {
    return (
      <div className="panel-page onboarding-page">
        <p className="loading-text">Ładowanie konfiguracji...</p>
      </div>
    );
  }

  return (
    <div className="panel-page onboarding-page">
      <PanelToast toast={toast} onClose={() => setToast(null)} />

      <div className="panel-header">
        <div className="panel-header-copy">
          <span className="panel-eyebrow">Konfiguracja startowa</span>
          <h1 className="panel-title">Uruchom konto w 3 krokach</h1>
          <p className="panel-subtitle">Własny link, kilka usług, cenniki promocyjne i realny tydzień pracy z wyjątkami.</p>
        </div>
      </div>

      <section className="section onboarding-wizard-shell">
        <div className="onboarding-stepper">
          {['Adres', 'Usługi', 'Grafik', 'Gotowe'].map((label, index) => (
            <div key={label} className={`onboarding-stepper-item ${step >= index ? 'is-active' : ''}`}>
              <span>{index + 1}</span>
              <strong>{label}</strong>
            </div>
          ))}
        </div>

        {step === 0 && (
          <div className="onboarding-stage">
            <div className="onboarding-stage-icon"><Link2 size={18} /></div>
            <h2>Ustaw własny adres rezerwacji</h2>
            <p>To link, który możesz wysłać klientkom albo wkleić na Instagram, stronę i wizytówkę Google.</p>

            <div className="form-group">
              <label htmlFor="onboardingSlug">Końcówka adresu</label>
              <div className="booking-slug-row">
                <span className="booking-slug-prefix">{resolveBookingOrigin()}/book/</span>
                <input
                  id="onboardingSlug"
                  type="text"
                  value={slugDraft}
                  onChange={(event) => {
                    setSlugDraft(normalizeBookingSlug(event.target.value));
                    setSlugError('');
                  }}
                />
              </div>
              {slugError && <p className="field-error">{slugError}</p>}
            </div>

            <div className="onboarding-summary-card">
              <strong>Podgląd linku</strong>
              <span>{bookingUrl}</span>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="onboarding-stage">
            <div className="onboarding-stage-icon"><Settings2 size={18} /></div>
            <h2>Zbuduj pierwszą ofertę</h2>
            <p>Dodaj kilka usług, ustaw promocje i skorzystaj z gotowych pakietów startowych, żeby szybciej ruszyć z konwersją.</p>

            <div className="onboarding-preset-grid">
              {SERVICE_PRESETS.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  className="onboarding-preset-card"
                  onClick={() => applyServicePreset(preset)}
                >
                  <strong>{preset.name}</strong>
                  <span>{preset.duration_minutes} min · od {preset.promo_price || preset.price} zł</span>
                </button>
              ))}
            </div>

            <div className="onboarding-bundle-grid">
              {SERVICE_BUNDLES.map((bundle) => (
                <div key={bundle.id} className="onboarding-bundle-card">
                  <div>
                    <strong>{bundle.label}</strong>
                    <p>{bundle.description}</p>
                  </div>
                  <span>{bundle.services.length} usług na start</span>
                  <button type="button" className="btn-secondary" disabled={saving} onClick={() => handleAddBundle(bundle)}>
                    Dodaj cały pakiet
                  </button>
                </div>
              ))}
            </div>

            <div className="onboarding-workspace-grid">
              <div className="onboarding-editor-card">
                <div className="auth-form-grid">
                  <div className="form-group auth-form-full">
                    <label htmlFor="serviceName">Nazwa usługi</label>
                    <input
                      id="serviceName"
                      value={serviceForm.name}
                      onChange={(event) => setServiceForm((prev) => ({ ...prev, name: event.target.value }))}
                    />
                  </div>

                  <div className="form-group auth-form-full">
                    <label htmlFor="serviceDescription">Opis</label>
                    <textarea
                      id="serviceDescription"
                      rows="3"
                      value={serviceForm.description}
                      onChange={(event) => setServiceForm((prev) => ({ ...prev, description: event.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="serviceDuration">Czas</label>
                    <input
                      id="serviceDuration"
                      type="number"
                      min="5"
                      value={serviceForm.duration_minutes}
                      onChange={(event) => setServiceForm((prev) => ({ ...prev, duration_minutes: Number(event.target.value) }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="servicePrice">Cena</label>
                    <input
                      id="servicePrice"
                      type="number"
                      min="1"
                      step="0.01"
                      value={serviceForm.price}
                      onChange={(event) => setServiceForm((prev) => ({ ...prev, price: event.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="servicePromoPrice">Cena promocyjna</label>
                    <input
                      id="servicePromoPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={serviceForm.promo_price}
                      onChange={(event) => setServiceForm((prev) => ({ ...prev, promo_price: event.target.value }))}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="servicePromoLabel">Etykieta promocji</label>
                    <input
                      id="servicePromoLabel"
                      value={serviceForm.promo_label}
                      onChange={(event) => setServiceForm((prev) => ({ ...prev, promo_label: event.target.value }))}
                    />
                  </div>
                </div>

                <div className="onboarding-addon-section">
                  <div className="onboarding-inline-header">
                    <strong>Dodatki do usługi</strong>
                    <button type="button" className="btn-ghost" onClick={addAddonRow}>
                      <Plus size={16} />
                      Dodaj dodatek
                    </button>
                  </div>

                  {serviceForm.addons.length === 0 && (
                    <p className="field-hint">Dodatki są opcjonalne. Przydają się przy zdjęciu stylizacji, ampułce albo konsultacji.</p>
                  )}

                  <div className="onboarding-addon-list">
                    {serviceForm.addons.map((addon, index) => (
                      <div key={`${index}-${addon.name}`} className="onboarding-addon-row">
                        <input
                          type="text"
                          placeholder="Nazwa dodatku"
                          value={addon.name}
                          onChange={(event) => updateAddon(index, 'name', event.target.value)}
                        />
                        <input
                          type="number"
                          min="5"
                          placeholder="Min"
                          value={addon.duration_minutes}
                          onChange={(event) => updateAddon(index, 'duration_minutes', Number(event.target.value))}
                        />
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Cena"
                          value={addon.price}
                          onChange={(event) => updateAddon(index, 'price', event.target.value)}
                        />
                        <button type="button" className="btn-ghost" onClick={() => removeAddon(index)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {serviceError && <p className="field-error">{serviceError}</p>}

                <div className="onboarding-footer-actions">
                  <button type="button" className="btn-secondary" disabled={saving} onClick={handleCreateService}>
                    {saving ? 'Zapisywanie...' : 'Dodaj usługę do oferty'}
                  </button>
                </div>
              </div>

              <div className="onboarding-editor-card">
                <div className="onboarding-inline-header">
                  <strong>Aktywne usługi</strong>
                  <span>{activeServices.length}</span>
                </div>

                <div className="onboarding-service-list">
                  {activeServices.length === 0 && (
                    <div className="onboarding-summary-card">
                      <strong>Oferta jest jeszcze pusta</strong>
                      <span>Dodaj minimum jedną usługę, żeby przejść dalej.</span>
                    </div>
                  )}

                  {activeServices.map((service) => {
                    const priceMeta = getServicePriceMeta(service);

                    return (
                      <div key={service.id} className="onboarding-service-item">
                        <div>
                          <strong>{service.name}</strong>
                          <span>
                            {service.duration_minutes} min · {formatPrice(priceMeta.effectivePrice)} zł
                            {priceMeta.hasPromo ? ` zamiast ${formatPrice(priceMeta.basePrice)} zł` : ''}
                          </span>
                        </div>
                        {service.promo_label && <span className="onboarding-promo-badge">{service.promo_label}</span>}
                        <p>{service.description || 'Usługa gotowa do publikacji w rezerwacji online.'}</p>
                        {Array.isArray(service.addons) && service.addons.length > 0 && (
                          <div className="onboarding-tag-row">
                            {service.addons.map((addon) => (
                              <span key={addon.id || addon.name} className="onboarding-tag">
                                {addon.name} · +{addon.duration_minutes} min
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="onboarding-stage">
            <div className="onboarding-stage-icon"><Clock3 size={18} /></div>
            <h2>Ustal realny tydzień pracy</h2>
            <p>Możesz ustawić kilka okien pracy w jednym dniu, np. przed i po przerwie, a niżej dodać pojedyncze albo seryjne wyjątki grafiku.</p>

            <div className="onboarding-preset-grid">
              {Object.entries(SCHEDULE_TEMPLATES).map(([key, template]) => (
                <button
                  key={key}
                  type="button"
                  className="onboarding-preset-card"
                  onClick={() => applyScheduleTemplate(key)}
                >
                  <strong>{template.label}</strong>
                  <span>{template.description}</span>
                </button>
              ))}
            </div>

            <div className="onboarding-workspace-grid onboarding-workspace-grid--wide">
              <div className="onboarding-editor-card">
                <div className="onboarding-inline-header">
                  <strong>Godziny pracy</strong>
                  <span>{activeScheduleWindows} okien</span>
                </div>

                <div className="onboarding-schedule-editor">
                  {scheduleDays.map((day) => (
                    <div key={day.day_of_week} className="onboarding-schedule-day-card">
                      <div className="onboarding-schedule-day-top">
                        <label className="onboarding-schedule-toggle">
                          <input
                            type="checkbox"
                            checked={day.is_active}
                            onChange={() => toggleDay(day.day_of_week)}
                          />
                          <span>{DAY_NAMES[day.day_of_week]}</span>
                        </label>

                        <button
                          type="button"
                          className="btn-ghost"
                          onClick={() => addDaySlot(day.day_of_week)}
                          disabled={!day.is_active}
                        >
                          <Plus size={16} />
                          Dodaj okno
                        </button>
                      </div>

                      {!day.is_active && <p className="field-hint">Dzień wolny od rezerwacji online.</p>}

                      {day.is_active && (
                        <div className="onboarding-day-slots">
                          {day.slots.map((slot, slotIndex) => (
                            <div key={`${day.day_of_week}-${slotIndex}`} className="onboarding-day-slot-row">
                              <input
                                type="time"
                                value={slot.start_time}
                                onChange={(event) => updateDaySlot(day.day_of_week, slotIndex, 'start_time', event.target.value)}
                              />
                              <span>-</span>
                              <input
                                type="time"
                                value={slot.end_time}
                                onChange={(event) => updateDaySlot(day.day_of_week, slotIndex, 'end_time', event.target.value)}
                              />
                              <button
                                type="button"
                                className="btn-ghost"
                                onClick={() => removeDaySlot(day.day_of_week, slotIndex)}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {scheduleError && <p className="field-error">{scheduleError}</p>}
              </div>

              <div className="onboarding-editor-card">
                <div className="onboarding-inline-header">
                  <strong>Blokady i wyjątki</strong>
                  <span>{blocks.length}</span>
                </div>

                <div className="onboarding-exception-grid">
                  {EXCEPTION_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      className="onboarding-exception-card"
                      disabled={saving}
                      onClick={() => handleApplyExceptionPreset(preset)}
                    >
                      <strong>{preset.label}</strong>
                      <span>{preset.description}</span>
                    </button>
                  ))}
                </div>

                <div className="auth-form-grid">
                  <div className="form-group">
                    <label htmlFor="blockReason">Typ blokady</label>
                    <select
                      id="blockReason"
                      value={breakForm.reason}
                      onChange={(event) => setBreakForm((prev) => ({ ...prev, reason: event.target.value }))}
                    >
                      {BLOCK_REASON_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="blockDate">Data</label>
                    <input
                      id="blockDate"
                      type="date"
                      value={breakForm.date}
                      onChange={(event) => setBreakForm((prev) => ({ ...prev, date: event.target.value }))}
                    />
                  </div>

                  {breakForm.reason !== 'Urlop' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="blockStart">Od</label>
                        <input
                          id="blockStart"
                          type="time"
                          value={breakForm.start_time}
                          onChange={(event) => setBreakForm((prev) => ({ ...prev, start_time: event.target.value }))}
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="blockEnd">Do</label>
                        <input
                          id="blockEnd"
                          type="time"
                          value={breakForm.end_time}
                          onChange={(event) => setBreakForm((prev) => ({ ...prev, end_time: event.target.value }))}
                        />
                      </div>
                    </>
                  )}
                </div>

                {breakError && <p className="field-error">{breakError}</p>}

                <div className="onboarding-footer-actions">
                  <button type="button" className="btn-secondary" disabled={saving} onClick={handleAddBlock}>
                    Dodaj blokadę czasu
                  </button>
                </div>

                <div className="onboarding-block-list">
                  {blocks.length === 0 && (
                    <p className="field-hint">Nie musisz dodawać blokad teraz. Ta sekcja przyda się na lunch, urlopy, dojazdy i krótsze dni pracy.</p>
                  )}

                  {blocks.map((block) => (
                    <div key={block.id} className="onboarding-block-item">
                      <div>
                        <strong>{block.reason || 'Blokada czasu'}</strong>
                        <span>{formatBlockLabel(block)}</span>
                      </div>
                      <button type="button" className="btn-ghost" onClick={() => handleDeleteBlock(block.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="onboarding-stage onboarding-stage--done">
            <div className="onboarding-stage-icon"><CheckCircle2 size={18} /></div>
            <h2>Konto gotowe do działania</h2>
            <p>Masz już własny link, kilka usług, promocje i grafik z wyjątkami, więc oferta jest bliżej realnej sprzedaży niż pustej konfiguracji.</p>

            <div className="onboarding-summary-grid">
              <div className="onboarding-summary-card">
                <strong>{professional?.booking_slug || slugDraft}</strong>
                <span>adres rezerwacji</span>
              </div>
              <div className="onboarding-summary-card">
                <strong>{activeServices.length}</strong>
                <span>aktywnych usług</span>
              </div>
              <div className="onboarding-summary-card">
                <strong>{activeScheduleDays}</strong>
                <span>dni pracy</span>
              </div>
            </div>

            <div className="onboarding-summary-grid">
              <div className="onboarding-summary-card">
                <strong>{activeScheduleWindows}</strong>
                <span>okien grafiku</span>
              </div>
              <div className="onboarding-summary-card">
                <strong>{blocks.length}</strong>
                <span>blokad czasu</span>
              </div>
              <div className="onboarding-summary-card">
                <strong>{totalAddons}</strong>
                <span>dodatków do usług</span>
              </div>
            </div>

            <div className="onboarding-summary-grid">
              <div className="onboarding-summary-card">
                <strong>{discountedServices}</strong>
                <span>usług z promocją</span>
              </div>
            </div>

            <div className="link-share-url">{bookingUrl}</div>

            <div className="onboarding-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(bookingUrl);
                    setToast({ type: 'success', message: 'Link do rezerwacji został skopiowany.' });
                  } catch {
                    setToast({ type: 'error', message: 'Nie udało się skopiować linku.' });
                  }
                }}
              >
                Skopiuj link
              </button>

              <button type="button" className="btn-primary" onClick={() => navigate('/panel', { replace: true })}>
                Przejdź do panelu
              </button>
            </div>
          </div>
        )}

        <div className="onboarding-footer-actions">
          {step > 0 && step < 3 && (
            <button type="button" className="btn-ghost" onClick={() => setStep((prev) => prev - 1)}>
              <ArrowLeft size={16} />
              Wstecz
            </button>
          )}
          {step < 3 && (
            <button type="button" className="btn-primary" disabled={saving} onClick={handleNext}>
              {saving ? 'Zapisywanie...' : 'Dalej'}
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
